import { useState } from "react";
import { toast } from "sonner";
import { Copy, UserPlus } from "lucide-react";
import { useFetch, Panel, Tag, PageHeader, Empty } from "@/components/Common";
import { api, errMsg, fmtTime } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const label = (p) => p.replace(/_/g, " ");

function PermGrid({ all, value, onChange, prefix }) {
  return (
    <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
      {all.filter((p) => p !== "manage_users").map((p) => (
        <label key={p} className="flex items-center gap-2 text-xs">
          <Checkbox checked={value.includes(p)} onCheckedChange={(c) => onChange(c ? [...value, p] : value.filter((x) => x !== p))} data-testid={`${prefix}-perm-${p}`} />
          <span className={p === "real_trade" ? "text-down" : ""}>{label(p)}</span>
        </label>
      ))}
    </div>
  );
}

function Invite({ all, onDone }) {
  const [f, setF] = useState({ email: "", name: "Berk", permissions: ["view_dashboard", "view_markets", "run_analysis", "backtest", "view_analytics", "emergency_stop"] });
  const [link, setLink] = useState(null);
  const submit = async () => {
    try { const { data } = await api.post("/users/invite", f); setLink(data.link); toast.success("Invitation created"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Invite user" right={<UserPlus size={16} className="text-primary" />}>
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <Input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email-input" />
        <Input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name-field" />
        <Button onClick={submit} data-testid="invite-submit-button">Create invite</Button>
      </div>
      <div className="mt-4"><PermGrid all={all} value={f.permissions} onChange={(p) => setF({ ...f, permissions: p })} prefix="invite" /></div>
      {link && (
        <div className="mt-4 flex items-center gap-2 rounded border bg-secondary p-3" data-testid="invite-link-box">
          <span className="break-all font-mono text-xs" data-testid="invite-link-text">{link}</span>
          <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }} data-testid="invite-copy-button"><Copy size={14} /></Button>
        </div>
      )}
      <p className="mt-2 text-xs text-muted-foreground">Share the single-use link privately (valid 72h). The invitee sets a password and must enrol MFA on first login.</p>
    </Panel>
  );
}

function UserRow({ u, all, reload }) {
  const [perms, setPerms] = useState(u.permissions);
  const patch = async (body, msg) => { try { await api.patch(`/users/${u.id}`, body); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded border p-4" data-testid={`user-row-${u.email}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2"><span className="font-semibold">{u.name || u.email}</span><span className="font-mono text-xs text-muted-foreground">{u.email}</span><Tag tone={u.role === "owner" ? "gold" : "blue"}>{u.role}</Tag><Tag tone={u.mfa_enabled ? "up" : "warn"}>{u.mfa_enabled ? "mfa on" : "mfa pending"}</Tag>{!u.active && <Tag tone="down">disabled</Tag>}</div>
        {u.role !== "owner" && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => patch({ reset_mfa: true }, "MFA reset")} data-testid={`user-reset-mfa-${u.email}`}>Reset MFA</Button>
            <Button size="sm" variant={u.active ? "destructive" : "outline"} onClick={() => patch({ active: !u.active }, u.active ? "User disabled" : "User enabled")} data-testid={`user-toggle-active-${u.email}`}>{u.active ? "Disable" : "Enable"}</Button>
          </div>
        )}
      </div>
      {u.role !== "owner" && (
        <div className="mt-3">
          <PermGrid all={all} value={perms} onChange={setPerms} prefix={`user-${u.email}`} />
          <Button size="sm" className="mt-3" onClick={() => patch({ permissions: perms }, "Permissions saved")} data-testid={`user-save-perms-${u.email}`}>Save permissions</Button>
        </div>
      )}
    </div>
  );
}

export default function Users() {
  const { data, reload } = useFetch("/users");
  if (!data) return null;
  const revoke = async (id) => { try { await api.delete(`/users/invite/${id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4">
      <PageHeader title="Users" sub="Invitation-only access with account-level permissions enforced on the server" />
      <Invite all={data.all_permissions} onDone={reload} />
      <Panel title="Accounts"><div className="space-y-3">{data.users.map((u) => <UserRow key={u.id + u.permissions.join()} u={u} all={data.all_permissions} reload={reload} />)}</div></Panel>
      <Panel title="Invitations">
        {data.invites.length ? data.invites.map((i) => (
          <div key={i.id} className="flex items-center justify-between border-b border-border/50 py-2 text-sm" data-testid={`invite-row-${i.email}`}>
            <span className="font-mono">{i.email}</span><Tag tone={i.status === "pending" ? "blue" : i.status === "accepted" ? "up" : "muted"}>{i.status}</Tag><span className="font-mono text-xs text-muted-foreground">exp {fmtTime(i.expires_at)}</span>
            {i.status === "pending" ? <Button size="sm" variant="ghost" onClick={() => revoke(i.id)} data-testid={`invite-revoke-${i.email}`}>Revoke</Button> : <span />}
          </div>
        )) : <Empty>No invitations yet</Empty>}
      </Panel>
    </div>
  );
}
